import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PODSubscriptionsModal from "@WhiteCap/Components/OSNSubscriptions/PodSubscriptionsModal";
import loadPodSubscribers from "@WhiteCap/Handlers/SubscriptionsDashboard/PodSubscriptions/GetPodSubscribers";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    buttontext = "buttontext",
    modalTitle = "modalTitle",
    changeCtaBtnPosition = "changeCtaBtnPosition",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttontext]: string;
        [fields.modalTitle]: string;
        [fields.changeCtaBtnPosition]: boolean;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSubscriptionCustomerId = state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId;
    const data =
        state.WhiteCap.subscriptionsDashboardData.osnType === "accountLevel"
            ? state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions
            : state.WhiteCap.subscriptionsDashboardData.jobSiteSubscriptionsById[currentSubscriptionCustomerId];

    return {
        data,
        currentSubscriptionCustomerId,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    loadPodSubscribers,
};

export interface ProofOfDeliveryStyles {
    title?: TypographyProps;
    description?: TypographyProps;
    ctaGridItem?: GridItemProps;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    content?: InjectableCss;
    key?: TypographyProps;
    value?: TypographyProps;
}

export const proofOfDeliveryStyles: ProofOfDeliveryStyles = {
    container: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 38px;
                        `,
                        null,
                        css`
                            margin-bottom: 50px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    title: {
        variant: "h4",
        weight: 325,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            margin-bottom: 0;
                        `,
                        null,
                        css`
                            font-size: 24px;
                             margin-bottom: 8px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    description: {
        variant: "h5",
        weight: 325,
        css: css`
            margin-bottom: 0;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            padding-bottom: 0;
                        `,
                        null,
                        css`
                            font-size: 24px;
                            padding-bottom: 15px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    ctaGridItem: {
        width: 12,
    },
    contentGridItem: {
        width: 12,
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 0;
                        `,
                        null,
                        css`
                            padding-bottom: 15px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    content: {
        css: css`
            width: 100%;
            background: #f7f8f9;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 11px 17px 20px 17px;
                        `,
                        null,
                        css`
                            padding: 20px 25px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    key: {
        weight: 325,
    },
    value: {
        weight: 500,
    },
};

const styles = proofOfDeliveryStyles;

const ProofOfDelivery: FC<Props> = ({ fields, data, loadPodSubscribers, currentSubscriptionCustomerId }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const closeModalHandler = (value: boolean) => {
        setIsModalOpen(value);
    };

    useEffect(() => {
        if (!isModalOpen) {
            if (!currentSubscriptionCustomerId) {
                return;
            }
            loadPodSubscribers({
                customerId: currentSubscriptionCustomerId,
                onError: (message: string) => {
                    toasterContext.addToast({
                        body: message,
                        messageType: "danger",
                    });
                },
            });
        }
    }, [isModalOpen]);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{fields.title}</Typography>
                <Typography {...styles.description}>{fields.description}</Typography>
            </GridItem>
            <GridItem {...styles.contentGridItem}>
                <StyledWrapper {...styles.content}>
                    <Typography {...styles.key}>Subscribers: </Typography>
                    <Typography {...styles.value}>
                        {data?.podOrderStatusSummary?.count}{" "}
                        {data?.podOrderStatusSummary?.frequency && `(${data?.podOrderStatusSummary?.frequency})`}
                    </Typography>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.ctaGridItem}>
                <Button onClick={() => setIsModalOpen(true)}>{fields.buttontext}</Button>
            </GridItem>
            {isModalOpen && (
                <PODSubscriptionsModal
                    isModalOpen={isModalOpen}
                    closeModalHandler={closeModalHandler}
                    changeCtaBtnPosition={fields.changeCtaBtnPosition}
                    modalTitle={fields.modalTitle}
                />
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProofOfDelivery),
    definition: {
        group: "My Account",
        displayName: "ProofOfDeliveryReporting",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.buttontext,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.modalTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.changeCtaBtnPosition,
                displayName: "Interchange Apply/Cancel CTA buttons position",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
